import { captureDomToPdf, PDF_TABLE_OVERRIDES_CSS, sanitizePdfFilename } from '@/utils/dom-to-pdf';

// The artifact is a live tiptap editor, so its editing affordances sit inside the captured subtree.
// The drag handle is positioned by JS, and `[data-active-cell]` outlines whichever cell holds the
// cursor, so both have been photographed into exported pages.
const ARTIFACT_EDITOR_CHROME_CSS = `
  .pdf-exporting .notion-drag-handle,
  .pdf-exporting .bubble-toolbar-container,
  .pdf-exporting .table-add-row-btn,
  .pdf-exporting .table-add-col-btn,
  .pdf-exporting .column-resize-handle,
  .pdf-exporting .cell-selection-overlay {
    display: none !important;
  }
  .pdf-exporting [data-active-cell] {
    outline: none !important;
  }
  .pdf-exporting .selectedCell {
    background: transparent !important;
  }
`;

// Wider than the panel, so the report's layout follows the document rather than the window size.
const REPORT_CAPTURE_WIDTH_PX = 1280;
// The artifact's own max reading width, mirroring `--main-content-max-width`.
const ARTIFACT_CAPTURE_WIDTH_PX = 1136;

// Both views build their body asynchronously, and capturing before that yields a blank or
// half-height page.
const waitForContent = async (el: HTMLElement, selector: string, timeoutMs = 5000): Promise<void> => {
  const start = performance.now();
  const isReady = () => el.isConnected && el.offsetHeight >= 20 && !!el.querySelector(selector);

  while (performance.now() - start < timeoutMs) {
    if (isReady()) return;
    await new Promise<void>(resolve => requestAnimationFrame(() => resolve()));
  }
  throw new Error('The document is not ready yet. Please wait for it to finish loading and try again.');
};

export const exportReportToPdf = (el: HTMLElement, filename: string): Promise<void> =>
  captureDomToPdf(el, {
    filename,
    exportOverridesCss: PDF_TABLE_OVERRIDES_CSS,
    waitForReady: target => waitForContent(target, '[data-block-card]'),
    minCaptureWidthPx: REPORT_CAPTURE_WIDTH_PX,
  });

export const exportArtifactToPdf = (el: HTMLElement, filename: string): Promise<void> =>
  captureDomToPdf(el, {
    filename,
    exportOverridesCss: `${PDF_TABLE_OVERRIDES_CSS}${ARTIFACT_EDITOR_CHROME_CSS}`,
    waitForReady: target => waitForContent(target, '.notion-editor'),
    minCaptureWidthPx: ARTIFACT_CAPTURE_WIDTH_PX,
  });

export { sanitizePdfFilename };
